(() => {
  if (customElements.get('custom-people-talking')) return;

  const toBoolean = (value) => value === 'true';
  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  class CustomPeopleTalking extends HTMLElement {
    constructor() {
      super();
      this.splide = null;
      this.retryTimer = null;
      this.resizeTimer = null;
      this.lastBreakpoint = '';
      this.abortController = new AbortController();
    }

    connectedCallback() {
      if (this.abortController.signal.aborted) this.abortController = new AbortController();

      window.addEventListener('resize', () => this.handleResize(), {
        signal: this.abortController.signal,
        passive: true,
      });

      this.syncSlider();
    }

    disconnectedCallback() {
      this.abortController.abort();
      window.clearTimeout(this.retryTimer);
      window.clearTimeout(this.resizeTimer);
      this.destroySlider();
    }

    get breakpoint() {
      if (window.matchMedia('(max-width: 699px)').matches) return 'mobile';
      if (window.matchMedia('(max-width: 999px)').matches) return 'tablet';
      return 'desktop';
    }

    get visibleItems() {
      return Math.max(1, toNumber(this.dataset[`${this.breakpoint}Items`], 1));
    }

    get slideCount() {
      return this.querySelectorAll('.custom-people-talking__slide:not(.splide__slide--clone)').length;
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        if (this.lastBreakpoint !== this.breakpoint) this.syncSlider(true);
      }, 120);
    }

    syncSlider(force = false) {
      const slider = this.querySelector('[data-people-talking-slider]');
      if (!slider) return;

      const activeBreakpoint = this.breakpoint;
      const needsSlider = this.slideCount > this.visibleItems;

      if (!needsSlider) {
        this.destroySlider();
        this.dataset.sliderState = 'static';
        this.lastBreakpoint = activeBreakpoint;
        return;
      }

      if (typeof window.Splide !== 'function') {
        window.clearTimeout(this.retryTimer);
        this.retryTimer = window.setTimeout(() => this.syncSlider(force), 100);
        return;
      }

      if (this.splide && !force && this.lastBreakpoint === activeBreakpoint) return;

      this.destroySlider();
      this.dataset.sliderState = 'active';

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const desktopItems = Math.max(1, toNumber(this.dataset.desktopItems, 3));
      const tabletItems = Math.max(1, toNumber(this.dataset.tabletItems, 2));
      const mobileItems = Math.max(1, toNumber(this.dataset.mobileItems, 1));

      this.splide = new window.Splide(slider, {
        type: 'slide',
        rewind: true,
        arrows: false,
        pagination: true,
        autoplay: !reduceMotion && toBoolean(this.dataset.autoplay),
        interval: toNumber(this.dataset.autoplaySpeed, 5000),
        speed: reduceMotion ? 0 : toNumber(this.dataset.transitionSpeed, 600),
        pauseOnHover: toBoolean(this.dataset.pauseOnHover),
        pauseOnFocus: true,
        resetProgress: false,
        perMove: 1,
        perPage: desktopItems,
        gap: '16px',
        padding: { right: '8vw' },
        mediaQuery: 'max',
        breakpoints: {
          999: {
            perPage: tabletItems,
            padding: { right: '8vw' },
          },
          699: {
            perPage: mobileItems,
            gap: '12px',
            padding: { right: '12vw' },
          },
        },
      });

      this.splide.mount();
      this.lastBreakpoint = activeBreakpoint;
    }

    destroySlider() {
      if (!this.splide) return;
      this.splide.destroy(true);
      this.splide = null;
    }

    selectBlock(block) {
      if (!this.splide || !block) return;
      const index = toNumber(block.dataset.peopleTalkingIndex, -1);
      if (index >= 0) this.splide.go(index);
    }
  }

  customElements.define('custom-people-talking', CustomPeopleTalking);

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest?.('custom-people-talking');
    section?.selectBlock(event.target.closest?.('.custom-people-talking__slide'));
  });
})();
